class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.announcement-bar__track');
    this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.delay = parseInt(this.dataset.delay) || 5000;

    this.currentIndex = 0;
    this.isPlaying = false;
    this.isTransitioning = false;
    this.timer = null;

    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  connectedCallback() {
    if (this.slides.length < 2) return;

    this.init();
    this.attachEvents();

    if (this.autoplay) {
      this.play();
    }
  }

  disconnectedCallback() {
    this.pause();
    this.detachEvents();
  }

  init() {
    // Set initial state
    this.slides.forEach((slide, index) => {
      if (index === this.currentIndex) {
        slide.classList.add('announcement-bar__slide--active');
        slide.setAttribute('aria-hidden', 'false');
        slide.style.zIndex = '1';
      } else {
        slide.classList.remove('announcement-bar__slide--active');
        slide.setAttribute('aria-hidden', 'true');
        slide.style.zIndex = '0';
      }
    });
  }

  attachEvents() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  detachEvents() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  play() {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.scheduleNextSlide();
  }

  pause() {
    this.isPlaying = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  scheduleNextSlide() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.next();
    }, this.delay);
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex, 'next');
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex, 'prev');
  }

  goToSlide(index, direction = 'next') {
    if (this.isTransitioning || index === this.currentIndex) return;
    this.isTransitioning = true;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    const exitClass = direction === 'next' ? 'announcement-bar__slide--exit-left' : 'announcement-bar__slide--exit-right';
    const enterClass = direction === 'next' ? 'announcement-bar__slide--enter-right' : 'announcement-bar__slide--enter-left';

    const hasFocus = currentSlide.contains(document.activeElement);

    nextSlide.classList.add(enterClass);
    nextSlide.setAttribute('aria-hidden', 'false');
    nextSlide.style.zIndex = '1';

    nextSlide.offsetHeight;

    if (hasFocus) {
      const focusableInNext = nextSlide.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (focusableInNext) {
        focusableInNext.focus();
      } else {
        nextSlide.setAttribute('tabindex', '-1');
        nextSlide.focus();
      }
    }

    // Start transition
    requestAnimationFrame(() => {
      currentSlide.classList.add(exitClass);
      currentSlide.classList.remove('announcement-bar__slide--active');
      currentSlide.setAttribute('aria-hidden', 'true');
      currentSlide.style.zIndex = '0';

      nextSlide.classList.remove(enterClass);
      nextSlide.classList.add('announcement-bar__slide--active');
    });

    // Handle transition end
    const onTransitionEnd = () => {
      currentSlide.style.transition = 'none';
      currentSlide.classList.remove(exitClass);
      nextSlide.classList.remove(enterClass);
      currentSlide.offsetHeight;
      currentSlide.style.transition = '';

      this.isTransitioning = false;
      this.currentIndex = index;

      if (this.isPlaying) {
        this.scheduleNextSlide();
      }
    };

    const transitionDuration = 1200;
    setTimeout(onTransitionEnd, transitionDuration);
  }

  // Event Handlers
  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else if (this.autoplay) {
      this.play();
    }
  }

  handleFocusIn() {
    this.pause();
  }

  handleFocusOut(event) {
    if (!this.contains(event.relatedTarget) && this.autoplay) {
      this.play();
    }
  }

  handleMouseEnter() {
    this.pause();
  }

  handleMouseLeave() {
    if (this.autoplay) {
      this.play();
    }
  }
}

customElements.define('announcement-bar', AnnouncementBar);